import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceImages = [
  '/assets/images/vicenzo-livingrm.jpg',
  '/assets/images/cortina-kitchen.jpg',
  '/assets/images/cortina-bath.jpg',
  '/assets/images/provence-dining.jpg',
  '/assets/images/vicenzo-wetbar.jpg',
  '/assets/images/positano-livingrm.jpg',
];

export const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src="/assets/images/vicenzo-wetbar.jpg"
          alt="Interior Design Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-center text-white max-w-3xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm tracking-[0.3em] uppercase mb-4"
          >
            What We Offer
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-5xl md:text-7xl font-serif mb-4"
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-white/80 text-lg max-w-xl mx-auto"
          >
            Full-service interior design from concept to completion
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Full Service Design</p>
            <h2 className="text-3xl md:text-4xl font-serif mb-6">
              Creating Beautiful Spaces, Effortlessly
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              From concept to completion, Pure Style offers comprehensive interior design services tailored to your unique style and needs. We handle every detail—sourcing the finest materials, coordinating deliveries, and managing installations—so you can simply enjoy the transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid - Alternating image + text rows */}
      <section className="bg-[#f5f4f0]">
        {services.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0 }}
            transition={{ duration: 0.5 }}
            data-testid={`service-card-${service.id}`}
            className={`grid grid-cols-1 lg:grid-cols-2 ${index % 2 === 1 ? 'lg:direction-rtl' : ''}`}
          >
            <div className={`relative h-[300px] lg:h-[400px] overflow-hidden ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
              <img
                src={serviceImages[index] || serviceImages[0]}
                alt={service.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className={`flex items-center p-10 md:p-16 bg-white ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
              <div>
                <span className="text-5xl font-serif text-bronze/20 block mb-2">0{index + 1}</span>
                <h3 className="text-2xl font-serif mb-4">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {service.description}
                </p>
                <div className="w-12 h-[2px] bg-bronze" />
              </div>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Process Timeline */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">How We Work</p>
            <h2 className="text-3xl md:text-4xl font-serif">Our Design Process</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { num: '01', title: 'Consultation', desc: 'We start with an in-depth conversation to understand your vision, lifestyle, preferences, and budget.' },
              { num: '02', title: 'Design Concept', desc: 'Our team develops detailed design concepts with mood boards, floor plans, and material selections for your review.' },
              { num: '03', title: 'Selection & Sourcing', desc: 'We source the finest furnishings, fabrics, and finishes from our exclusive network of industry partners.' },
              { num: '04', title: 'Installation & Reveal', desc: 'We coordinate and manage every delivery and installation detail for a seamless, stress-free reveal day.' },
            ].map((step, index) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="relative bg-[#f5f4f0] rounded-2xl p-8 group hover:bg-bronze hover:text-white transition-all duration-500"
              >
                <div className="text-4xl font-serif text-bronze/30 group-hover:text-white/30 mb-4 transition-colors">{step.num}</div>
                <h3 className="text-lg font-serif mb-3">{step.title}</h3>
                <p className="text-muted-foreground group-hover:text-white/80 text-sm leading-relaxed transition-colors">{step.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-[2px] bg-border z-10" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 md:py-28 bg-[#f5f4f0]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-7">
                  <img
                    src="/assets/images/cortina-dining.jpg"
                    alt="Dining Room Design"
                    className="w-full h-[400px] object-cover rounded-2xl"
                  />
                </div>
                <div className="col-span-5">
                  <img
                    src="/assets/images/vicenzo-winecellar.jpg"
                    alt="Wine Cellar Design"
                    className="w-full h-[400px] object-cover rounded-2xl"
                  />
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Why Choose Us</p>
              <h2 className="text-3xl md:text-4xl font-serif mb-8">
                What Sets Pure Style Apart
              </h2>
              <div className="space-y-5">
                {[
                  { title: 'Personalized Approach', desc: 'Every design is tailored to your personality, lifestyle, and the way you live in your home.' },
                  { title: 'Exclusive Industry Access', desc: 'Access to premium furnishings, fabrics, and materials through our network of top manufacturers.' },
                  { title: 'Budget Conscious', desc: 'We help you achieve stunning results while being mindful of your investment.' },
                  { title: 'Complete Project Management', desc: 'From ordering to installation, we handle every logistical detail so you don\'t have to.' },
                  { title: 'Neal Communities Partnership', desc: 'As part of the Neal family, we bring unmatched home building and design expertise.' },
                ].map((item, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="w-6 h-6 rounded-full bg-bronze/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-bronze" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 overflow-hidden">
        <img
          src="/assets/images/positano-livingrm.jpg"
          alt="Living Room"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 container-custom">
          <div className="max-w-2xl mx-auto text-center text-white">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0 }}
              className="text-3xl md:text-5xl font-serif mb-6"
            >
              Ready to Get Started?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ delay: 0.1 }}
              className="text-white/80 mb-10 max-w-lg mx-auto"
            >
              Schedule a consultation and let's begin creating your dream space together.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contact"
                data-testid="services-cta-btn"
                className="inline-flex items-center justify-center gap-2 bg-bronze text-white hover:bg-bronze/90 px-8 py-4 text-sm tracking-widest uppercase transition-colors rounded-full"
              >
                Schedule Consultation
                <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+19413281233"
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white/10 px-8 py-4 text-sm tracking-widest uppercase transition-colors rounded-full"
              >
                <Phone size={16} />
                (941) 328-1233
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
